//#region FUNCTION movePropertiesToPayload

/**
 * Moves all properties from a parsed JSON string into a `payload` object, except for the ones explicitly preserved.
 *
 * This utility is useful for restructuring actions or objects where you want to isolate metadata (like `type`)
 * from the rest of the data for cleaner display or processing.
 *
 * @template T - The expected shape of the parsed object, where all values are strings.
 * @template K - Keys of `T` that should be preserved outside of the payload.
 *
 * @param value - A JSON string representing an object with string values.
 * @param preserveKeys - An array of keys whose values should be kept at the top level.
 *
 * @returns An object where the preserved keys remain at the top level and the rest are nested under a `payload` key.
 *
 * @example
 * ```ts
 * const json = '{"type":"LOGIN","user":"john.doe","role":"admin"}';
 * const result = movePropertiesToPayload(json, ['type']);
 * // result => { type: "LOGIN", payload: { user: "john.doe", role: "admin" } }
 * ```
 */
export function movePropertiesToPayload<
  T extends Record<string, string>,
  K extends keyof T
>(
  value: string,
  preserveKeys: readonly K[],
): Pick<T, K> & { payload: Omit<T, K> } & Object {
  const action = JSON.parse(value);
  const payload = {} as Omit<T, K>;
  const preserved = {} as Pick<T, K>;
  for (const key in action) {
    if (preserveKeys.includes(key as K)) {
      preserved[key as K] = action[key] as T[K];
    } else {
      payload[key as Exclude<keyof T, K>] = action[key] as T[Exclude<keyof T, K>];
    }
  }
  return {
    ...preserved,
    payload,
  };
}

//#endregion
